import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cartTotals, clearCart, formatCAD, getCart } from "@/lib/utils-cart";
import { GatewayBadge } from "@/components/Badges";
import { ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cart] = useState(getCart());
  const t = cartTotals(cart);
  const [gateway, setGateway] = useState("square");
  const [config, setConfig] = useState({ square_enabled: true, paypal_enabled: true, square_configured: false, paypal_configured: false });
  const [form, setForm] = useState({
    customer_email: user?.email || "",
    customer_name: user?.full_name || "",
    street: "", city: "", province: "ON", postal_code: "", country: "CA",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/payment-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return toast.error("Cart is empty");
    if (!user) {
      toast.error("Sign in to place your order");
      return nav("/login");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity })),
        gateway,
        customer_email: form.customer_email,
        customer_name: form.customer_name,
        shipping_address: {
          street: form.street, city: form.city, province: form.province, postal_code: form.postal_code, country: form.country,
        },
        notes: form.notes,
      });
      // Mock/sandbox confirmation flow (UI fully built; switch to real Square/PayPal SDKs when keys are added)
      await api.post(`/payments/${data.payment_id}/confirm`, { outcome: "approved", gateway_transaction_id: `MOCK-${gateway.toUpperCase()}-${data.order_id.slice(0, 8)}` });
      clearCart();
      nav(`/order/${data.order_id}/success`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <PublicLayout>
        <div className="max-w-xl mx-auto p-20 text-center">
          <h1 className="font-display text-2xl font-bold text-primary">Your cart is empty.</h1>
          <Link to="/marketplace" className="v-btn-primary mt-6" data-testid="empty-checkout-cta">Shop now</Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 space-y-8">
          <div>
            <h1 className="font-display text-3xl font-black tracking-tight text-primary">Checkout</h1>
            <p className="mt-1 text-sm text-muted-foreground">Centralized payments through Vendora. No vendor-side gateways.</p>
          </div>

          {/* Contact */}
          <Section title="Contact">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Email" type="email" value={form.customer_email} onChange={(v) => setForm({...form, customer_email: v})} required testid="checkout-email" />
              <Field label="Full name" value={form.customer_name} onChange={(v) => setForm({...form, customer_name: v})} required testid="checkout-name" />
            </div>
          </Section>

          {/* Shipping */}
          <Section title="Shipping address">
            <div className="grid gap-3">
              <Field label="Street address" value={form.street} onChange={(v) => setForm({...form, street: v})} required testid="checkout-street" />
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="City" value={form.city} onChange={(v) => setForm({...form, city: v})} required testid="checkout-city" />
                <div>
                  <label className="label-eyebrow mb-2 block">Province</label>
                  <select value={form.province} onChange={(e) => setForm({...form, province: e.target.value})} className="v-input" data-testid="checkout-province">
                    {["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"].map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <Field label="Postal code" value={form.postal_code} onChange={(v) => setForm({...form, postal_code: v.toUpperCase()})} required testid="checkout-postal" />
              </div>
            </div>
          </Section>

          {/* Payment Method */}
          <Section title="Payment method">
            <div className="grid sm:grid-cols-2 gap-3">
              <PaymentChoice
                id="square"
                enabled={config.square_enabled}
                selected={gateway === "square"}
                onSelect={() => setGateway("square")}
                label="Pay with Square"
                sub={config.square_configured ? "Live sandbox" : "Sandbox / mock mode"}
                testid="pay-square"
              />
              <PaymentChoice
                id="paypal"
                enabled={config.paypal_enabled}
                selected={gateway === "paypal"}
                onSelect={() => setGateway("paypal")}
                label="Pay with PayPal"
                sub={config.paypal_configured ? "Live sandbox" : "Sandbox / mock mode"}
                testid="pay-paypal"
              />
            </div>

            <div className="mt-4 flex items-start gap-2 border border-border bg-muted p-3 rounded-sm text-sm">
              <ShieldCheck size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium text-primary">Centralized & secured</div>
                <div className="text-xs text-muted-foreground mt-0.5">All payments flow through Vendora's platform account. Refunds, chargebacks, and commissions are handled internally.</div>
              </div>
            </div>
          </Section>

          <Section title="Order notes (optional)">
            <textarea rows={3} className="v-input" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Anything we should know?" data-testid="checkout-notes" />
          </Section>

          <button type="submit" disabled={submitting} className="v-btn-primary w-full text-base py-3" data-testid="checkout-submit">
            {submitting ? "Processing…" : <>Pay {formatCAD(t.total)} <ArrowRight size={16} /></>}
          </button>
        </form>

        <aside className="v-card h-fit lg:sticky lg:top-24">
          <h2 className="font-display text-xl font-bold text-primary">Order summary</h2>
          <div className="mt-4 divide-y divide-border">
            {cart.map((c) => (
              <div key={c.product_id} className="py-3 flex gap-3 items-center">
                <img src={c.image} className="w-12 h-12 object-cover bg-muted rounded-sm" alt={c.title} />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-primary line-clamp-1">{c.title}</div>
                  <div className="text-xs text-muted-foreground font-mono">× {c.quantity}</div>
                </div>
                <div className="text-sm font-mono">{formatCAD(c.price * c.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm">
            <Row label="Subtotal" value={formatCAD(t.subtotal)} />
            <Row label="Tax (HST)" value={formatCAD(t.tax)} />
            <Row label="Shipping" value={t.shipping ? formatCAD(t.shipping) : "Free"} />
            <div className="pt-3 mt-1 border-t border-border flex items-baseline justify-between">
              <span className="font-display font-bold text-primary">Total (CAD)</span>
              <span className="font-display font-black text-2xl text-foreground" data-testid="checkout-total">{formatCAD(t.total)}</span>
            </div>
          </div>
        </aside>
      </section>
    </PublicLayout>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-lg font-semibold text-primary mb-3">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="label-eyebrow mb-2 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="v-input" data-testid={testid} />
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><span className="font-mono">{value}</span></div>;
}

function PaymentChoice({ id, enabled, selected, onSelect, label, sub, testid }) {
  return (
    <button type="button" disabled={!enabled} onClick={onSelect} data-testid={testid}
      className={`text-left v-card transition-all relative ${selected ? "border-secondary border-2" : ""} ${!enabled ? "opacity-50 pointer-events-none" : ""}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-semibold text-primary">{label}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
        </div>
        <GatewayBadge gateway={id} />
      </div>
      {selected && <div className="absolute top-2 right-2"><CheckCircle2 size={16} className="text-secondary" /></div>}
    </button>
  );
}
